import React, { useEffect, useState, useCallback } from "react";
import { Star, FileText, Images, Check, Plus, Eye, EyeOff, MessageSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

export default function ReviewsAdmin() {
  const [list, setList] = useState([]);
  const [imp, setImp] = useState({ author: "", source: "airbnb", rating: 5, title: "", text: "", location: "" });
  const load = useCallback(() => api.get("/admin/reviews").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, data) => { await api.patch(`/admin/reviews/${id}`, data); load(); };
  const respond = async (id) => {
    const response = window.prompt("Public response to this review:");
    if (response != null) await patch(id, { response });
  };
  const del = async (id) => { await api.delete(`/admin/reviews/${id}`); load(); };
  const importReview = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/reviews/import", { ...imp, rating: Number(imp.rating) }); toast.success("Review imported"); setImp({ author: "", source: "airbnb", rating: 5, title: "", text: "", location: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        {list.map((r) => (
          <div key={r.id} className="card-lux p-5" data-testid={`admin-review-${r.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-forest">{r.author}</span>
                  <span className="text-xs text-gold">{"★".repeat(r.rating)}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${r.status === "approved" ? "bg-green-100 text-green-700" : r.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-500"}`}>{r.status}</span>
                  {r.verified && <span className="text-[11px] text-green-700">✓ Verified · {r.source}</span>}
                </div>
                <div className="font-serif text-lg text-forest mt-1">{r.title}</div>
                <p className="text-sm text-stone-600 mt-1">{r.text}</p>
                {r.response && <div className="mt-2 border-l-2 border-gold pl-3 text-sm text-stone-500">Reply: {r.response}</div>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {r.status !== "approved" && <button onClick={() => patch(r.id, { status: "approved" })} data-testid={`approve-review-${r.id}`} className="text-xs px-3 py-1.5 rounded-full bg-green-50 text-green-700 flex items-center gap-1"><Check size={13} /> Approve</button>}
              <button onClick={() => patch(r.id, { featured: !r.featured })} className={`text-xs px-3 py-1.5 rounded-full flex items-center gap-1 ${r.featured ? "bg-gold/20 text-gold" : "bg-stone-100 text-stone-600"}`}><Star size={13} /> {r.featured ? "Featured" : "Feature"}</button>
              <button onClick={() => patch(r.id, { status: r.status === "hidden" ? "approved" : "hidden" })} className="text-xs px-3 py-1.5 rounded-full bg-stone-100 text-stone-600 flex items-center gap-1">{r.status === "hidden" ? <Eye size={13} /> : <EyeOff size={13} />} {r.status === "hidden" ? "Unhide" : "Hide"}</button>
              <button onClick={() => respond(r.id)} className="text-xs px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 flex items-center gap-1"><MessageSquare size={13} /> Respond</button>
              <button onClick={() => del(r.id)} className="text-xs px-3 py-1.5 rounded-full bg-red-50 text-red-600 flex items-center gap-1"><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={importReview} className="card-lux p-6 h-fit" data-testid="import-review-form">
        <h3 className="font-serif text-xl text-forest mb-4">Import a review</h3>
        <p className="text-xs text-stone-500 mb-4">Add reviews collected from OTAs. Imported reviews show a verified-source badge.</p>
        <input required placeholder="Reviewer name" value={imp.author} onChange={(e) => setImp({ ...imp, author: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm mb-3" />
        <div className="grid grid-cols-2 gap-3 mb-3">
          <select value={imp.source} onChange={(e) => setImp({ ...imp, source: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm">
            {["airbnb", "booking", "expedia", "vrbo", "google"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={imp.rating} onChange={(e) => setImp({ ...imp, rating: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm">
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} stars</option>)}
          </select>
        </div>
        <input placeholder="Title" value={imp.title} onChange={(e) => setImp({ ...imp, title: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm mb-3" />
        <textarea required rows={3} placeholder="Review text" value={imp.text} onChange={(e) => setImp({ ...imp, text: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm mb-3" />
        <button className="btn-primary w-full"><Plus size={15} /> Import review</button>
      </form>
    </div>
  );
}
